import React, {useEffect, useRef, useState} from 'react';
import OeTip from './OeTip';
import {docUrl, loadDocs} from './api';

// Beleg-PDFs ansehen, drucken, herunterladen (Original `openPdf`/`showPdf`,
// Zeilen 2225–2267). weclapp legt Auftragsbestätigung, Lieferscheine und den
// Sammelbeleg als Dokumente ab; der Server reicht sie durch, damit der
// weclapp-Zugang den Browser nie erreicht.

/** Kurzname eines Dokuments für die Reiterbeschriftung. */
function pdfShortName(n) {
    return String(n || 'PDF').replace(/\.pdf$/i, '').replace(/-Kdnr-\d+$/i, '');
}

export default function PdfOverlay({show, orderId, source, onClose}) {
    const [docs, setDocs] = useState([]);
    const [current, setCurrent] = useState(0);
    const [ready, setReady] = useState(false);
    const [state, setState] = useState('idle');
    const frameRef = useRef(null);

    useEffect(() => {
        if (!show || !source) {
            return;
        }
        let abgebrochen = false;
        setState('lädt');
        setDocs([]);
        setCurrent(0);
        loadDocs(orderId, source)
            .then((data) => {
                if (abgebrochen) {
                    return;
                }
                const list = data.docs || [];
                setDocs(list);
                // Startdokument ist der Sammelbeleg, sonst das erste Dokument.
                const sammel = list.findIndex((d) => d.sammel);
                setCurrent(Math.max(0, sammel));
                setState(list.length ? 'ok' : 'leer');
            })
            .catch(() => {
                if (!abgebrochen) {
                    setState('fehler');
                }
            });
        return () => {
            abgebrochen = true;
        };
    }, [show, orderId, source && source.type, source && source.id]);

    useEffect(() => {
        setReady(false);
    }, [current, docs]);

    if (!show) {
        return null;
    }

    const doc = docs[current] || null;
    const src = doc ? docUrl(orderId, doc.docId, source) : 'about:blank';

    // Herunterladen über einen kurzlebigen Verweis — so bleibt die Seite stehen
    // (kein Wechsel der Adresse, kein Neuladen).
    const download = () => {
        if (!doc) {
            return;
        }
        const a = document.createElement('a');
        a.href = docUrl(orderId, doc.docId, {...source, download: true});
        a.download = doc.name || 'beleg.pdf';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const print = () => {
        const f = frameRef.current;
        try {
            f.contentWindow.focus();
            f.contentWindow.print();
        } catch (e) {
            // Rückfall, wenn der PDF-Betrachter aus einer anderen Quelle lädt.
            window.open(src, '_blank', 'noopener');
        }
    };

    return (
        <div className="overlay show" role="dialog" aria-modal="true" aria-label="Belege / PDF">
            <div className="modal pdfmodal">
                <h2>
                    Belege / PDF
                    <span className="pdftabs">
                        {docs.map((d, i) => (
                            <OeTip key={d.docId} tip={d.sammel ? 'Sammelbeleg — enthält alle Belege des Vorgangs.' : d.name}>
                                <button
                                    type="button"
                                    className={`pdftab${d.sammel ? ' sammel' : ''}${i === current ? ' on' : ''}`}
                                    onClick={() => setCurrent(i)}
                                >
                                    {pdfShortName(d.name)}{d.sammel ? ' ★' : ''}
                                </button>
                            </OeTip>
                        ))}
                    </span>
                </h2>

                {state === 'lädt' ? <div className="muted" style={{padding: '8px 0'}}>Belege werden geladen…</div> : null}
                {state === 'leer' ? (
                    <div className="muted" style={{padding: '8px 0'}}>
                        Noch keine PDFs zu diesem Beleg (sie entstehen beim Festschreiben in weclapp).
                    </div>
                ) : null}
                {state === 'fehler' ? (
                    <div className="status err" style={{padding: '8px 0'}}>Die Belegliste konnte nicht geladen werden.</div>
                ) : null}

                <div className="pdfwrap">
                    <iframe
                        id="oe-pdfFrame"
                        ref={frameRef}
                        className={ready ? 'ready' : ''}
                        title="Beleg-PDF"
                        src={src}
                        onLoad={() => setReady(true)}
                    />
                </div>

                <div className="btns">
                    <OeTip tip="Schließt die Vorschau. Am Beleg ändert sich nichts.">
                        <button type="button" onClick={onClose}>Schließen</button>
                    </OeTip>
                    <OeTip tip="Speichert das gezeigte PDF unverändert auf diesem Rechner.">
                        <button type="button" disabled={!doc} onClick={download}>⬇ Herunterladen</button>
                    </OeTip>
                    <OeTip tip="Öffnet den Druckdialog des Browsers für das gezeigte PDF.">
                        <button
                            type="button"
                            className="primary"
                            style={{background: 'var(--wc)', borderColor: 'var(--wc)'}}
                            disabled={!doc}
                            onClick={print}
                        >
                            🖨 Drucken
                        </button>
                    </OeTip>
                </div>
            </div>
        </div>
    );
}
